import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import GameBoard from './components/GameBoard';
import HUD from './components/HUD';
import { rules } from './data/rules';
import { getSpanishMeaning } from './data/spanishTranslations';

const HIGH_SCORES_KEY = 'wordMunchersHighScores';
const MAX_HIGH_SCORES = 10;

const SUBJECT_OPTIONS = [
  { value: '', label: 'Select One' },
  { value: 'all', label: 'All Subjects' },
  { value: 'spelling', label: 'Spelling' },
  { value: 'grammar', label: 'Grammar' },
  { value: 'spanish', label: 'Spanish' }
];

function getRulesForSubject(subject) {
  if (!subject || subject === 'all') return subject === 'all' ? rules : [];
  return rules.filter((r) => r.category === subject);
}

function loadHighScores() {
  try {
    const stored = localStorage.getItem(HIGH_SCORES_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (e) {
    // ignore
  }
  return [];
}

function App() {
  // Game state
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [currentRule, setCurrentRule] = useState(null);
  const [gameState, setGameState] = useState('START'); // START, PLAYING, LEVEL_COMPLETE, GAME_OVER
  const [playerPosition, setPlayerPosition] = useState({ row: 2, col: 2 });
  const [monsters, setMonsters] = useState([]);
  const [gridCells, setGridCells] = useState([]);
  const [clearedCells, setClearedCells] = useState([]);
  const [gameStartTime, setGameStartTime] = useState(null);

  // High scores (persisted in localStorage)
  const [highScores, setHighScores] = useState(() => loadHighScores());
  const [initials, setInitials] = useState('');
  const [scoreSubmitted, setScoreSubmitted] = useState(false);

  // Subject selection (Spelling, Grammar, Spanish, All Subjects)
  const [selectedSubject, setSelectedSubject] = useState('');

  // Teaching prompts: wrong-answer feedback and hints (pause game while shown)
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [hintMessage, setHintMessage] = useState(null);
  const [hintsRemaining, setHintsRemaining] = useState(3);

  // Game configuration
  const GRID_ROWS = 5;
  const GRID_COLS = 6;
  const MONSTER_SPAWN_DELAY = 10000; // 10 seconds in milliseconds

  // Initialize a new level (optional levelOverride, subjectOverride, and previousRule to avoid repeats)
  const initializeLevel = useCallback((levelOverride, subjectOverride, previousRule) => {
    const effectiveLevel = levelOverride !== undefined ? levelOverride : level;
    const subject = subjectOverride !== undefined ? subjectOverride : selectedSubject;
    const subjectRules = getRulesForSubject(subject);
    if (subjectRules.length === 0) {
      setCurrentRule({ rule: 'No content for this subject', correctAnswers: [], incorrectAnswers: [] });
      setGridCells([]);
      return;
    }
    // Avoid repeating the same rule on consecutive levels (for any subject)
    const rulesToPick =
      previousRule && subjectRules.length > 1
        ? subjectRules.filter((r) => r.rule !== previousRule.rule)
        : subjectRules;
    const randomIndex = Math.floor(Math.random() * rulesToPick.length);
    const randomRule = rulesToPick[randomIndex];
    setCurrentRule(randomRule);

    // Generate grid with mix of correct and incorrect answers
    const cells = [];
    const numCorrect = 8 + Math.floor(effectiveLevel / 3); // More correct answers as level increases
    const numIncorrect = (GRID_ROWS * GRID_COLS) - numCorrect;

    // Add correct answers
    for (let i = 0; i < numCorrect; i++) {
      const randomCorrect = randomRule.correctAnswers[
        Math.floor(Math.random() * randomRule.correctAnswers.length)
      ];
      cells.push({ word: randomCorrect, isCorrect: true });
    }

    // Add incorrect answers
    for (let i = 0; i < numIncorrect; i++) {
      const randomIncorrect = randomRule.incorrectAnswers[
        Math.floor(Math.random() * randomRule.incorrectAnswers.length)
      ];
      cells.push({ word: randomIncorrect, isCorrect: false });
    }

    // Shuffle the cells
    const shuffledCells = cells.sort(() => Math.random() - 0.5);
    
    setGridCells(shuffledCells);
    setClearedCells([]);
    setPlayerPosition({ row: 2, col: 2 }); // Center of grid
    setMonsters([]);
    setGameStartTime(Date.now());
  }, [level, selectedSubject]);

  // Start the game
  const startGame = () => {
    setGameState('PLAYING');
    setScore(0);
    setLives(3);
    setLevel(1);
    setScoreSubmitted(false);
    setInitials('');
    setFeedbackMessage(null);
    setHintMessage(null);
    setHintsRemaining(3);
    initializeLevel(1, selectedSubject);
  };

  // Add a score to high scores (top 10, sorted by score descending)
  const addHighScore = (initialsStr, scoreValue) => {
    const trimmed = initialsStr.slice(0, 3).toUpperCase().replace(/[^A-Z]/g, '');
    if (!trimmed) return;
    const next = [...highScores, { initials: trimmed, score: scoreValue }]
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_HIGH_SCORES);
    setHighScores(next);
    try {
      localStorage.setItem(HIGH_SCORES_KEY, JSON.stringify(next));
    } catch (e) {
      // ignore
    }
    setScoreSubmitted(true);
  };

  const handleInitialsChange = (e) => {
    const val = e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 3).toUpperCase();
    setInitials(val);
  };

  // Spawn monsters after delay
  useEffect(() => {
    if (gameState !== 'PLAYING' || !gameStartTime) return;

    const timer = setTimeout(() => {
      // Spawn 1-2 monsters based on level
      const numMonsters = level > 10 ? 2 : 1;
      const newMonsters = [];

      for (let i = 0; i < numMonsters; i++) {
        // Spawn at random edge position
        const edge = Math.floor(Math.random() * 4); // 0=top, 1=right, 2=bottom, 3=left
        let row, col;

        switch (edge) {
          case 0: // top
            row = 0;
            col = Math.floor(Math.random() * GRID_COLS);
            break;
          case 1: // right
            row = Math.floor(Math.random() * GRID_ROWS);
            col = GRID_COLS - 1;
            break;
          case 2: // bottom
            row = GRID_ROWS - 1;
            col = Math.floor(Math.random() * GRID_COLS);
            break;
          default: // left
            row = Math.floor(Math.random() * GRID_ROWS);
            col = 0;
        }

        newMonsters.push({ id: i, row, col });
      }

      setMonsters(newMonsters);
    }, MONSTER_SPAWN_DELAY);

    return () => clearTimeout(timer);
  }, [gameState, gameStartTime, level, GRID_COLS, GRID_ROWS]);

  // Hint: press H to show a teaching hint (3 per game)
  useEffect(() => {
    if (gameState !== 'PLAYING' || hintsRemaining <= 0 || feedbackMessage || hintMessage) return;

    const handleKeyDown = (e) => {
      if (e.key !== 'h' && e.key !== 'H') return;
      e.preventDefault();
      const stillVisibleCorrect = gridCells
        .map((cell, index) => (cell.isCorrect && !clearedCells.includes(index) ? { word: cell.word } : null))
        .filter(Boolean);
      if (stillVisibleCorrect.length === 0) return;

      const pick = stillVisibleCorrect[Math.floor(Math.random() * stillVisibleCorrect.length)];
      const word = pick.word;
      const isSpanish = currentRule?.category === 'spanish';
      const meaning = isSpanish ? getSpanishMeaning(word) : undefined;
      const message = isSpanish && meaning
        ? `"${word}" is the Spanish word for ${meaning}.`
        : `One correct answer is "${word}."`;
      setHintMessage(message);
      setHintsRemaining((prev) => prev - 1);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState, hintsRemaining, feedbackMessage, hintMessage, gridCells, clearedCells, currentRule?.category]);

  // Handle eating a cell
  const eatCell = () => {
    if (feedbackMessage || hintMessage) return;

    const cellIndex = playerPosition.row * GRID_COLS + playerPosition.col;
    
    // Check if cell is already cleared
    if (clearedCells.includes(cellIndex)) {
      return;
    }

    const cell = gridCells[cellIndex];

    if (cell.isCorrect) {
      // Correct answer!
      setScore(prevScore => prevScore + 10);
      setClearedCells(prev => [...prev, cellIndex]);

      // Check if level is complete (all correct answers eaten)
      const totalCorrect = gridCells.filter(c => c.isCorrect).length;
      const clearedCorrect = clearedCells.filter(i => gridCells[i].isCorrect).length + 1;

      if (clearedCorrect >= totalCorrect) {
        // Level complete! Wait for player to click "Let's Go!"
        setGameState('LEVEL_COMPLETE');
      }
    } else {
      // Wrong answer: show teaching prompt, then apply penalty
      const isSpanish = currentRule?.category === 'spanish';
      const translation = isSpanish ? getSpanishMeaning(cell.word) : undefined;
      const message = isSpanish && translation
        ? `Sorry, this word means ${translation}.`
        : 'Sorry, this is not correct.';
      setFeedbackMessage(message);

      setScore(prevScore => Math.max(0, prevScore - 10));
      setLives(prevLives => prevLives - 1);
      
      if (lives - 1 <= 0) {
        setGameState('GAME_OVER');
      }
    }
  };

  // Check collision with monsters
  useEffect(() => {
    if (gameState !== 'PLAYING') return;

    const collision = monsters.some(
      monster => monster.row === playerPosition.row && monster.col === playerPosition.col
    );

    if (collision) {
      setLives(prevLives => prevLives - 1);
      setScore(prevScore => Math.max(0, prevScore - 5));
      
      if (lives - 1 <= 0) {
        setGameState('GAME_OVER');
      } else {
        // Respawn player at center
        setPlayerPosition({ row: 2, col: 2 });
      }
    }
  }, [playerPosition, monsters, gameState, lives]);

  return (
    <div className="App">
      <div className="game-container">
        {gameState === 'START' && (
          <div className="menu-screen">
            <h1>Word Munchers</h1>
            <p>Learn Spelling, Grammar & Spanish!</p>
            <div className="menu-start-row">
              <div className="subject-selection">
                <label htmlFor="subject-select">Pick a Subject:</label>
                <select
                  id="subject-select"
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  className="subject-select"
                >
                  {SUBJECT_OPTIONS.map((opt) => (
                    <option key={opt.value || 'placeholder'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={startGame}
                disabled={!selectedSubject}
                className="start-button start-button-small"
              >
                Start Game
              </button>
            </div>
            <div className="instructions">
              <h3>How to Play:</h3>
              <p>Use arrow keys to move</p>
              <p>Press SPACE to eat a word</p>
              <p>Eat only the correct answers!</p>
              <p>Avoid the monsters!</p>
              <p>Press H for a hint (3 per game)</p>
            </div>
            <div className="high-scores-section">
              <h3>High Scores</h3>
              <table className="high-scores-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Initials</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {highScores.slice(0, MAX_HIGH_SCORES).map((entry, index) => (
                    <tr key={`${entry.initials}-${entry.score}-${index}`}>
                      <td>{index + 1}</td>
                      <td>{entry.initials}</td>
                      <td>{entry.score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {highScores.length === 0 && (
                <p className="no-scores-message">No high scores yet. Play to add one!</p>
              )}
            </div>
          </div>
        )}

        {gameState === 'PLAYING' && (
          <>
            <HUD 
              level={level}
              score={score}
              lives={lives}
              rule={currentRule?.rule || ''}
              hintsRemaining={hintsRemaining}
            />
            <GameBoard
              rows={GRID_ROWS}
              cols={GRID_COLS}
              gridCells={gridCells}
              clearedCells={clearedCells}
              playerPosition={playerPosition}
              setPlayerPosition={setPlayerPosition}
              monsters={monsters}
              setMonsters={setMonsters}
              onEatCell={eatCell}
              gameState={gameState}
              paused={!!(feedbackMessage || hintMessage)}
            />
            {feedbackMessage && (
              <div className="teaching-modal-overlay">
                <div className="teaching-modal">
                  <p className="teaching-modal-message">{feedbackMessage}</p>
                  <button type="button" className="resume-level-button" onClick={() => setFeedbackMessage(null)}>
                    Resume Level
                  </button>
                </div>
              </div>
            )}
            {hintMessage && (
              <div className="teaching-modal-overlay">
                <div className="teaching-modal">
                  <p className="teaching-modal-message">{hintMessage}</p>
                  <button type="button" className="resume-level-button" onClick={() => setHintMessage(null)}>
                    Resume Level
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {gameState === 'LEVEL_COMPLETE' && (
          <div className="level-complete-screen">
            <h1>Level Complete! 🎉</h1>
            <p className="complete-score">Score: {score}</p>
            <p className="complete-message">Are you ready for the next level?</p>
            <button
              onClick={() => {
                const nextLevel = level + 1;
                setLevel(prev => prev + 1);
                setGameState('PLAYING');
                initializeLevel(nextLevel, selectedSubject, currentRule);
              }}
              className="lets-go-button"
            >
              Let's Go!
            </button>
          </div>
        )}

        {gameState === 'GAME_OVER' && (
          <div className="game-over-screen">
            <h1>Game Over!</h1>
            <p className="final-score">Final Score: {score}</p>
            <p className="final-level">Level Reached: {level}</p>
            {!scoreSubmitted ? (
              <div className="high-score-entry">
                <label htmlFor="initials-input">Enter your initials (3 letters):</label>
                <input
                  id="initials-input"
                  type="text"
                  maxLength={3}
                  value={initials}
                  onChange={handleInitialsChange}
                  placeholder="AAA"
                  className="initials-input"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => {
                    setGameState('START');
                    setScoreSubmitted(false);
                    setInitials('');
                  }}
                  className="skip-button"
                >
                  Skip
                </button>
                <button
                  type="button"
                  onClick={() => addHighScore(initials, score)}
                  disabled={initials.length < 1}
                  className="submit-score-button"
                >
                  Add to High Scores
                </button>
              </div>
            ) : (
              <p className="score-saved-message">Score saved!</p>
            )}
            <div className="high-scores-section">
              <h3>High Scores</h3>
              <table className="high-scores-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Initials</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {highScores.slice(0, MAX_HIGH_SCORES).map((entry, index) => (
                    <tr key={`${entry.initials}-${entry.score}-${index}`}>
                      <td>{index + 1}</td>
                      <td>{entry.initials}</td>
                      <td>{entry.score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {highScores.length === 0 && (
                <p className="no-scores-message">No high scores yet. Play to add one!</p>
              )}
            </div>
            <div className="game-over-buttons">
              <button
                onClick={startGame}
                className={`restart-button${!scoreSubmitted ? ' inactive' : ''}`}
                disabled={!scoreSubmitted}
              >
                Play Again
              </button>
              <button
                onClick={() => {
                  setGameState('START');
                  setScoreSubmitted(false);
                  setInitials('');
                }}
                disabled={!scoreSubmitted}
                className={`home-page-button${!scoreSubmitted ? ' inactive' : ''}`}
              >
                Go Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;